import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle } from 'react-feather';
import { Badge, Button, Form, Modal } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  createFeatureFlagAPI,
  getFeatureFlagsAPI,
  getTenantsListAPI,
  toggleFeatureFlagAPI,
  updateFlagScopeAPI,
} from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import DataTableWrapper from '../../../Components/shared/DataTableWrapper';
import ConfirmModal from '../../../Components/shared/ConfirmModal';
import { platformUrl } from '../../../utils/mhNav';
import styles from './FeatureFlags.module.scss';

const initialScopeDraft = () => ({
  allPlans: false,
  starter: false,
  growth: false,
  enterprise: false,
  tenantIds: [],
});

export default function FeatureFlags() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newFlag, setNewFlag] = useState({ key: '', description: '', owner: '' });
  const [scopeOpen, setScopeOpen] = useState(false);
  const [scopeFlag, setScopeFlag] = useState(null);
  const [scopeDraft, setScopeDraft] = useState(initialScopeDraft);
  const [tenantOptions, setTenantOptions] = useState([]);
  const [toggle1Open, setToggle1Open] = useState(false);
  const [toggle2Open, setToggle2Open] = useState(false);
  const [pendingToggle, setPendingToggle] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getFeatureFlagsAPI();
      setRows(Array.isArray(res.data) ? res.data : []);
      setHasLoaded(true);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openScope = async (f) => {
    setScopeFlag(f);
    setScopeDraft({
      allPlans: !!f.scope?.allPlans,
      starter: !!f.scope?.starter,
      growth: !!f.scope?.growth,
      enterprise: !!f.scope?.enterprise,
      tenantIds: [...(f.scope?.tenantIds || [])],
    });
    try {
      const tr = await getTenantsListAPI({ limit: 100, page: 1 });
      const items = tr.data?.items || [];
      setTenantOptions(
        items.map((t) => ({
          value: t.id,
          label: `${t.tenantCode} — ${t.companyName}`,
        }))
      );
    } catch {
      setTenantOptions([]);
    }
    setScopeOpen(true);
  };

  const saveScope = async () => {
    if (!scopeFlag) return;
    try {
      await updateFlagScopeAPI(scopeFlag.id, {
        ...scopeDraft,
        tenantIds: scopeDraft.tenantIds,
      });
      toast.success('Scope updated');
      setScopeOpen(false);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const requestToggle = (f) => {
    setPendingToggle({
      id: f.id,
      key: f.key,
      dangerous: !!f.dangerous,
      nextEnabled: !f.enabled,
    });
    setToggle1Open(true);
  };

  const runToggle = async () => {
    if (!pendingToggle) return;
    try {
      await toggleFeatureFlagAPI(pendingToggle.id);
      toast.success(`Flag ${pendingToggle.nextEnabled ? 'enabled' : 'disabled'}`);
      setToggle1Open(false);
      setToggle2Open(false);
      setPendingToggle(null);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const onFirstToggleConfirm = () => {
    if (pendingToggle?.dangerous) {
      setToggle1Open(false);
      setToggle2Open(true);
    } else {
      runToggle();
    }
  };

  const tenantSelectValue = tenantOptions.filter((o) => scopeDraft.tenantIds.includes(o.value));

  const columns = [
    { name: 'Flag key', selector: (r) => r.key, sortable: true, grow: 2 },
    { name: 'Description', selector: (r) => r.description, grow: 2 },
    { name: 'Environment', selector: (r) => r.environment, width: '120px' },
    {
      name: 'Scope',
      cell: (r) => (
        <div>
          <div>{r.scopeLabel || '—'}</div>
          <Button size="sm" variant="link" className="p-0" onClick={() => openScope(r)}>
            Edit
          </Button>
        </div>
      ),
      grow: 1,
    },
    {
      name: 'Status',
      cell: (r) => (
        <div className="d-flex align-items-center gap-2">
          <Badge bg={r.enabled ? 'success' : 'secondary'}>{r.enabled ? 'On' : 'Off'}</Badge>
          <Button size="sm" variant="outline-primary" onClick={() => requestToggle(r)}>
            Toggle
          </Button>
        </div>
      ),
      width: '200px',
    },
    { name: 'Owner', selector: (r) => r.owner, width: '180px' },
    {
      name: 'Actions',
      cell: (r) =>
        r.dangerous ? (
          <Badge bg="warning" text="dark">
            Dangerous
          </Badge>
        ) : (
          <span className="text-muted">—</span>
        ),
      width: '120px',
    },
  ];

  if (loading && !hasLoaded) {
    return (
      <div>
        <PageHeader
          title="Feature flags"
          breadcrumbs={[{ label: 'Platform', path: platformUrl('dashboard') }, { label: 'Settings', path: platformUrl('settings') }, { label: 'Flags' }]}
        />
        <LoadingSkeleton rows={6} cols={4} />
      </div>
    );
  }

  if (error && !hasLoaded) {
    return (
      <div>
        <PageHeader
          title="Feature flags"
          breadcrumbs={[{ label: 'Platform', path: platformUrl('dashboard') }, { label: 'Settings', path: platformUrl('settings') }, { label: 'Flags' }]}
        />
        <EmptyState icon={AlertTriangle} title="Could not load" description={error.message} actionLabel="Retry" onAction={load} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Feature flags"
        breadcrumbs={[{ label: 'Platform', path: platformUrl('dashboard') }, { label: 'Settings', path: platformUrl('settings') }, { label: 'Flags' }]}
        primaryAction={{ label: 'New flag', onClick: () => setCreateOpen(true) }}
      />

      {error && hasLoaded && (
        <div className="alert alert-warning small mb-2">Last refresh failed: {error.message}</div>
      )}

      <DataTableWrapper columns={columns} data={rows} loading={false} showSearch keyField="id" />

      <Modal show={createOpen} onHide={() => setCreateOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create flag</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>Key</Form.Label>
            <Form.Control value={newFlag.key} onChange={(e) => setNewFlag((s) => ({ ...s, key: e.target.value }))} placeholder="ff.platform.my_flag" />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Description</Form.Label>
            <Form.Control
              value={newFlag.description}
              onChange={(e) => setNewFlag((s) => ({ ...s, description: e.target.value }))}
              as="textarea"
              rows={2}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Owner</Form.Label>
            <Form.Control value={newFlag.owner} onChange={(e) => setNewFlag((s) => ({ ...s, owner: e.target.value }))} placeholder="team@meetinghub.ai" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={async () => {
              try {
                await createFeatureFlagAPI(newFlag);
                toast.success('Flag created');
                setCreateOpen(false);
                setNewFlag({ key: '', description: '', owner: '' });
                load();
              } catch (e) {
                toast.error(e.message);
              }
            }}
          >
            Create
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={scopeOpen} onHide={() => setScopeOpen(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Scope — {scopeFlag?.key}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className={styles.scopeHint}>Choose plan rollout and optional tenant allow-list.</p>
          <Form.Check
            className="mb-2"
            type="checkbox"
            label="All plans"
            checked={scopeDraft.allPlans}
            onChange={(e) => setScopeDraft((s) => ({ ...s, allPlans: e.target.checked }))}
          />
          <Form.Check
            className="mb-2"
            type="checkbox"
            label="Starter"
            checked={scopeDraft.starter}
            onChange={(e) => setScopeDraft((s) => ({ ...s, starter: e.target.checked }))}
          />
          <Form.Check
            className="mb-2"
            type="checkbox"
            label="Growth"
            checked={scopeDraft.growth}
            onChange={(e) => setScopeDraft((s) => ({ ...s, growth: e.target.checked }))}
          />
          <Form.Check
            className="mb-2"
            type="checkbox"
            label="Enterprise"
            checked={scopeDraft.enterprise}
            onChange={(e) => setScopeDraft((s) => ({ ...s, enterprise: e.target.checked }))}
          />
          <Form.Label className="mt-2">Specific tenants</Form.Label>
          <Select
            isMulti
            options={tenantOptions}
            value={tenantSelectValue}
            onChange={(opts) =>
              setScopeDraft((s) => ({
                ...s,
                tenantIds: (opts || []).map((o) => o.value),
              }))
            }
            placeholder="Search tenants…"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={saveScope}>Save scope</Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        isOpen={toggle1Open}
        onClose={() => {
          setToggle1Open(false);
          setPendingToggle(null);
        }}
        onConfirm={onFirstToggleConfirm}
        title="Toggle feature flag?"
        message={
          pendingToggle
            ? `Change "${pendingToggle.key}" to ${pendingToggle.nextEnabled ? 'ON' : 'OFF'}?`
            : ''
        }
        confirmLabel="Continue"
      />

      <ConfirmModal
        isOpen={toggle2Open}
        onClose={() => {
          setToggle2Open(false);
          setPendingToggle(null);
        }}
        onConfirm={runToggle}
        title="Dangerous flag"
        message="This flag is marked dangerous. Proceed with the toggle?"
        confirmLabel="Toggle anyway"
        danger
      />
    </div>
  );
}
